<template>
  <button :style="styleObject" :class="classObject">
    <slot>{{ text }}</slot>
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { ColorSchemes, SizeNames } from '@/utils/enum'
import { validateColorScheme, validateSizeName } from '@/utils/propValidation'
import { $t } from '@/composables/translation'

/**
 * A button which can use a default slot or :text prop for it's content
 */
export default defineComponent({
  props: {
    outline: { type: Boolean, default: false },
    block: { type: Boolean, default: false },
    rich: { type: Boolean, default: false },
    rounded: { type: Boolean, default: false },
    uppercase: { type: Boolean, default: false },
    size: {
      type: String,
      default: SizeNames.MD,
      validator(value) {
        return validateSizeName(value)
      },
    },
    color: {
      type: String,
      default: ColorSchemes.DEFAULT,
      validator(value) {
        return validateColorScheme(value)
      },
    },
    borderColor: {
      type: String,
      default: '#e8e8ed',
    },
    text: { type: String, default: $t('Submit') },
  },
  computed: {
    classObject() {
      return {
        'base-button btn': true,
        [`btn-${this.size}`]: true,
        [`btn-${this.color}`]: true,
        outline: this.outline,
        'btn-block': this.block,
        'btn-rich': this.rich,
        rounded: this.rounded,
        uppercase: this.uppercase,
      }
    },
    styleObject() {
      return this.outline ? { border: `1px solid ${this.borderColor} !important` } : {}
    },
  },
})
</script>

<style scoped lang="scss">
@import 'node_modules/bootstrap-sass/assets/stylesheets/bootstrap/variables';

.base-button {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  word-spacing: normal !important;

  :deep(a) {
    color: inherit;
  }

  &.btn-lg {
    font-size: 16px;
    padding-top: 15px;
    padding-bottom: 15px;
    i,
    img {
      margin-right: 16px !important;
    }
  }

  &.btn-sm,
  .btn-md {
    padding: 6px 12px;
  }
  &.btn-xs {
    min-height: 29px;
  }

  &.outline {
    @apply tw-shadow-sm;
    border-radius: 2px;

    &:hover {
      @apply tw-shadow;
    }

    &.btn-default {
      &:hover,
      &:active,
      &:focus {
        /* prevent the standard darkening on hover */
        background: $btn-default-bg;
      }
    }
  }

  &.rounded {
    border-radius: 6px;
  }

  &.btn-block {
    justify-content: center;
  }

  &.uppercase {
    text-transform: uppercase;
  }
}
</style>
